import { Input, InputProps, NonCancelableCustomEvent } from '@amzn/awsui-components-react-v3';
import { BaseChangeDetail } from '@amzn/awsui-components-react-v3/polaris/input/interfaces';
import { BaseKeyDetail } from '@amzn/awsui-components-react-v3/polaris/internal/events';
import { omit } from 'lodash';
import React, { ReactElement, RefObject, useCallback, useEffect, useState } from 'react';

export interface ScanInputProps extends InputProps {
  ref?: RefObject<InputProps.Ref>;
  type?: InputProps.Type;
  value: string;
  onInput: (input: string) => void;
  clearOnEnter?: boolean;
  onEnter: (value: string) => void;
}

/**
 * Custom Component for showing barcode scannable input.
 * @param {function} onScan called when enter is pressed on the scan input
 * @param {boolean} clearOnEnter specifies if the input needs to be cleared on enter
 */

function ScanInput(props: ScanInputProps): ReactElement {
  const { onEnter, clearOnEnter = true, onInput, value } = props;
  const [input, setInput] = useState<string>('');
  useEffect(() => {
    if (value && input !== value) {
      setInput(value);
    }
  }, [input, value]);

  const handleChange = useCallback(
    (event: NonCancelableCustomEvent<BaseChangeDetail>) => {
      setInput(event.detail.value);
      if (onInput) {
        onInput(event.detail.value);
      }
    },
    [onInput]
  );

  const handleScanComplete = (event: CustomEvent<BaseKeyDetail>) => {
    if (event.detail.key === 'Enter' && input?.trim() !== '') {
      onEnter(input.trim());
      if (clearOnEnter) {
        setInput('');
      }
    }
  };

  return (
    <Input
      value={input}
      onChange={handleChange}
      onKeyDown={handleScanComplete}
      data-test-id="barcode-scan"
      {...omit(props, ['value'])}
    />
  );
}

export default React.memo(ScanInput);
